import React, { useMemo } from "react";
import { Box, Typography } from "@material-ui/core";
import { formatNota } from "../../utils/formato";

const W = 720;
const H = 340;
const padL = 52;
const padR = 36;
const padT = 44;
const padB = 56;
const MAX_SPREAD = 210;
const MAX_POR_COLA = 32;

const seleccionarFueraDeCaja = (puntajes, q1, q3) => {
  if (q1 == null || q3 == null || !puntajes.length) return [];
  const bajos = puntajes
    .filter((p) => Number(p.total) < q1)
    .sort((a, b) => Number(a.total) - Number(b.total))
    .slice(0, MAX_POR_COLA);
  const altos = puntajes
    .filter((p) => Number(p.total) > q3)
    .sort((a, b) => Number(b.total) - Number(a.total))
    .slice(0, MAX_POR_COLA);
  return [...bajos, ...altos];
};

const textoTooltipEstudiante = (p) => {
  const nom = (p.nombre || "").trim();
  if (nom) return nom;
  const c = (p.correo || "").trim();
  const at = c.indexOf("@");
  if (at > 0) return c.slice(0, at);
  return c || "—";
};

const puntosConDesplazamiento = (lista) => {
  const bin = (t) => Math.round(Number(t) * 100) / 100;
  const groups = new Map();
  lista.forEach((p) => {
    const b = bin(p.total);
    if (!groups.has(b)) groups.set(b, []);
    groups.get(b).push(p);
  });

  const out = [];
  groups.forEach((arr) => {
    const sorted = [...arr].sort((a, b) => String(a.correo).localeCompare(String(b.correo)));
    const n = sorted.length;
    const step = n <= 1 ? 0 : Math.min(7.5, MAX_SPREAD / (n - 1));
    sorted.forEach((p, i) => {
      const dx = n <= 1 ? 0 : (i - (n - 1) / 2) * step;
      out.push({ ...p, dx });
    });
  });
  return out;
};

const GraficaDistribucionBoxPlot = ({ programaNombre, puntajes = [], stats }) => {
  const { q1, median, q3, whiskerLow, whiskerHigh } = stats;
  const hasBox = q1 != null && q3 != null && median != null && whiskerLow != null && whiskerHigh != null;

  const puntosVisibles = useMemo(() => {
    if (!hasBox) return [];
    const sel = seleccionarFueraDeCaja(puntajes, q1, q3);
    return puntosConDesplazamiento(sel);
  }, [puntajes, q1, q3, hasBox]);

  const { yAt, cx, ticks } = useMemo(() => {
    const totals = puntajes.map((p) => p.total).filter((n) => Number.isFinite(n));
    const lo = totals.length ? Math.min(...totals) : 1;
    const hi = totals.length ? Math.max(...totals) : 5;
    const minV = Math.min(0.5, lo - 0.15);
    const maxV = Math.max(5.2, hi + 0.15);
    const plotH = H - padT - padB;
    const yAtFn = (v) => padT + ((maxV - v) / (maxV - minV)) * plotH;
    const tickVals = [0, 1, 2, 3, 4, 5].filter((t) => t >= minV - 0.01 && t <= maxV + 0.01);
    return {
      yAt: yAtFn,
      cx: W / 2,
      ticks: tickVals.length ? tickVals : [minV, maxV],
    };
  }, [puntajes]);

  if (!puntajes.length) {
    return (
      <Box py={6} textAlign="center" bgcolor="#fafafa" borderRadius={10}>
        <Typography variant="body2" color="textSecondary">
          No hay resultados de pruebas para este programa, o el filtro no coincide con los datos devueltos por la API.
        </Typography>
      </Box>
    );
  }

  const boxLeft = cx - 46;
  const boxRight = cx + 46;
  const q1y = yAt(q1);
  const q3y = yAt(q3);
  const top = Math.min(q1y, q3y);
  const boxH = Math.abs(q3y - q1y) || 2;

  return (
    <Box position="relative">
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        style={{ gap: 14, marginBottom: 10, paddingLeft: 8, paddingRight: 8 }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <span style={{ width: 22, height: 3, background: "#ea580c", borderRadius: 1 }} />
          <Typography variant="caption" style={{ color: "#334155", fontWeight: 600, fontSize: 12 }}>
            Mediana
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <span style={{ width: 26, height: 0, borderTop: "3px dashed #0d47a1", display: "inline-block" }} />
          <Typography variant="caption" style={{ color: "#0d47a1", fontWeight: 700, fontSize: 12 }}>
            Q1 programa
          </Typography>
        </Box>
      </Box>

      {hasBox && puntosVisibles.length === 0 ? (
        <Box py={3} px={2} textAlign="center" bgcolor="#f8fafc" borderRadius={10} mb={1}>
          <Typography variant="body2" style={{ color: "#475569" }}>
            Ningún estudiante queda fuera del rango central (entre Q1 y Q3). Todos los últimos intentos están dentro de la caja;
            por eso no se dibujan puntos.
          </Typography>
        </Box>
      ) : null}

      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" style={{ display: "block", maxHeight: 400 }} preserveAspectRatio="xMidYMid meet">
        {ticks.map((t) => {
          const yy = yAt(t);
          return (
            <g key={t}>
              <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="#eef2f7" strokeWidth={1} />
              <text x={padL - 10} y={yy + 4} textAnchor="end" fill="#64748b" fontSize={12} fontWeight={600}>
                {formatNota(t)}
              </text>
            </g>
          );
        })}

        {hasBox && q1 != null ? (
          <line
            x1={padL}
            y1={yAt(q1)}
            x2={W - padR}
            y2={yAt(q1)}
            stroke="#0d47a1"
            strokeWidth={2.5}
            strokeDasharray="10 7"
          />
        ) : null}

        {puntosVisibles.map((p) => {
          const x = cx + p.dx;
          const yy = yAt(p.total);
          const esBajo = Number(p.total) < q1;
          const fill = esBajo ? "rgba(220, 38, 38, 0.88)" : "rgba(22, 163, 74, 0.9)";
          const stroke = esBajo ? "#991b1b" : "#14532d";
          return (
            <g key={p.correo} transform={`translate(${x}, ${yy})`} style={{ cursor: "help" }}>
              <circle cx={0} cy={0} r={4} fill={fill} stroke={stroke} strokeWidth={0.7} />
              <title>{textoTooltipEstudiante(p)}</title>
            </g>
          );
        })}

        {hasBox ? (
          <>
            <line x1={cx} y1={yAt(q3)} x2={cx} y2={yAt(whiskerHigh)} stroke="#1e293b" strokeWidth={2} strokeLinecap="round" />
            <line x1={cx - 10} y1={yAt(whiskerHigh)} x2={cx + 10} y2={yAt(whiskerHigh)} stroke="#1e293b" strokeWidth={2} strokeLinecap="round" />
            <line x1={cx} y1={yAt(q1)} x2={cx} y2={yAt(whiskerLow)} stroke="#1e293b" strokeWidth={2} strokeLinecap="round" />
            <line x1={cx - 10} y1={yAt(whiskerLow)} x2={cx + 10} y2={yAt(whiskerLow)} stroke="#1e293b" strokeWidth={2} strokeLinecap="round" />
            <rect
              x={boxLeft}
              y={top}
              width={boxRight - boxLeft}
              height={boxH}
              fill="rgba(255,255,255,0.92)"
              stroke="#0f172a"
              strokeWidth={2.2}
              rx={3}
            />
            <line x1={boxLeft} y1={yAt(median)} x2={boxRight} y2={yAt(median)} stroke="#ea580c" strokeWidth={3} strokeLinecap="round" />
          </>
        ) : null}

        <text x={cx} y={H - 12} textAnchor="middle" fill="#334155" fontSize={12} fontWeight={700}>
          {(programaNombre || "Programa").length > 52 ? `${(programaNombre || "").slice(0, 49)}…` : programaNombre || ""}
        </text>
      </svg>
    </Box>
  );
};

export default GraficaDistribucionBoxPlot;
